import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select, Row, Col, notification, Divider, Popconfirm, TreeSelect } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { IAddEditUser } from '../../../../models/users';
import { RootState, useAppDispatch } from '../../../../app/store';
import { createUser, editUser, fetchUserById } from '../usersApi';
import { NOTIFICATION_TYPES, ROLES } from '../../../../utils/constant';
import BreadcrumbComponent from '../../../../components/Breadcrumb';
import { useTranslation } from 'react-i18next';
import { IBreadcrumbItem } from '../../../../models/breadcrumb';
import '../style.css';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { fetchMinistries, fetchRoles } from '../../../master/masterApi';
import { findTopLevelParents, convertToTreeData } from '../../../../utils/commonFunctions';

const { Option } = Select;
const { SHOW_PARENT } = TreeSelect;

interface AddEditUserComponentProps {
    isEdit?: boolean;
}

const AddEditUserComponent: React.FC<AddEditUserComponentProps> = ({ isEdit = false }) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { userId } = useParams<{ userId: string }>();
    const [isSuperAdminRoleSelected, setIsSuperAdminRoleSelected] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const { t } = useTranslation();
    const loginUser = useSelector((state: RootState) => state.auth.loginUser);
    const { roles, ministries } = useSelector((state: RootState) => state.masterData);
    const [selectedMinistry, setSelectedMinistry] = useState<string[] | null>(null); // Define the type as string[] or null
    let masterRoles = roles;
    const treeData = convertToTreeData(ministries);
    const [disableRole, setDisableRole] = useState(false);
    const breadcrumbItems: Array<IBreadcrumbItem> = [
        { title: t('home'), path: '/home' },
        { title: t('menu.setup.user-management'), path: '/setup/user-management' },
        { title: isEdit ? t('user-management.edit-user') : t('user-management.add-user') }
    ];
    const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

    useEffect(() => {
        if (userId) {
            setDisableRole(loginUser?.user?.id === userId ? true : false);
        }
    }, [userId]);

    //On form load update filed as per operation (add/edit)
    useEffect(() => {
        if (isEdit && userId) {
            dispatch(fetchUserById(parseInt(userId)))
                .unwrap()
                .then((response) => {
                    if (response?.status === 'success') {
                        const user = response.data;
                        form.setFieldsValue({
                            firstName: user.firstname,
                            lastName: user.lastname,
                            email: user.email,
                            roleId: user.role_id,
                            ministryId: !(user?.nodes) ? undefined : user.nodes.map((node: any) => node.id.toString())
                        });

                        // Check if the user's role is Super Admin
                        if (user.role_id === ROLES.SUPER_ADMIN) {
                            setIsSuperAdminRoleSelected(true);
                        }
                    }
                });
        }
    }, []);

    //Fetch roles master list if not already loaded
    useEffect(() => {
        if (!(roles?.length)) {
            dispatch(fetchRoles());
        }
    }, [dispatch, roles]);

    //Fetch ministries master list if not already loaded
    useEffect(() => {
        if (!(ministries?.length)) {
            dispatch(fetchMinistries());
        }
    }, [dispatch, ministries]);

    //Handle form submit event then call api method
    const handleSubmit = (values: any) => {
        const user: IAddEditUser = {
            id: userId ? parseInt(userId) : 0,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            role_id: values.roleId,
            node_id: values?.ministryId ? values?.ministryId.map(Number) : [],
            is_active: 1,
        };

        const action =
            isEdit && userId
                ? editUser(user)
                : createUser(user);

        dispatch(action)
            .unwrap()
            .then((response) => {
                form.resetFields();
                if (response?.status === 'success') {
                    notification[NOTIFICATION_TYPES.SUCCESS]({ message: t('success'), description: response?.message });
                    navigate('/setup/user-management');
                }
            });
    };

    //Handle form field change events
    const onFormValuesChange = (changedValues: any) => {
        if (changedValues?.roleId !== undefined) {
            setIsSuperAdminRoleSelected(changedValues.roleId === ROLES.SUPER_ADMIN);
        }
        setIsFormDirty(form.isFieldsTouched());
    };

    if (loginUser?.user?.role_id === ROLES.DATA_MANAGER) {
        masterRoles = masterRoles?.filter(item => item.id !== ROLES.SUPER_ADMIN);
    }

    const expandAllKeys = (nodes: any) => {
        const keys: string[] = [];
        const traverse = (nodeList: any) => {
            nodeList.forEach((node: any) => {
                keys.push(node.key);
                if (node.children) {
                    traverse(node.children);
                }
            });
        };
        traverse(nodes);
        return keys;
    };

    return (
        <>
            <Row className='mainTextArea' style={{ width: '100%' }}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} >
                    <Row gutter={[16, 16]} style={{ borderBottom: '1px solid #CCC', marginBottom: '10px', paddingBottom: '10px' }}>
                        <Col xs={24} sm={24} md={12} lg={12} xl={12} >
                            <h4>{isEdit ? t('user-management.edit-user') : t('user-management.add-user')}</h4>
                            <BreadcrumbComponent items={breadcrumbItems} separator='>' />
                        </Col>
                    </Row>
                </Col>
                <Form
                    style={{ width: '100%' }}
                    form={form}
                    layout="vertical"
                    initialValues={{ firstName: '', lastName: '', email: '', roleId: undefined, ministryId: undefined }}
                    onFinish={handleSubmit}
                    onValuesChange={onFormValuesChange}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                label={t('user-management.first-name')}
                                name="firstName"
                                rules={[
                                    { required: true, message: t('user-management.first-name.req.error') },
                                    { min: 2, message: t('user-management.first-name.min.error') },
                                    { max: 50, message: t('user-management.first-name.max.error') },
                                ]}
                            >
                                <Input placeholder={t('user-management.first-name-placeholder')} min={2} max={50} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={t('user-management.last-name')}
                                name="lastName"
                                rules={[
                                    { required: true, message: t('user-management.last-name.req.error') },
                                    { min: 2, message: t('user-management.last-name.min.error') },
                                    { max: 50, message: t('user-management.last-name.max.error') },
                                ]}
                            >
                                <Input placeholder={t('user-management.last-name-placeholder')} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                label={t('user-management.email-add')}
                                name="email"
                                rules={[
                                    { required: true, message: t('user-management.email.error') },  // required field
                                    { type: 'email', message: t('user-management.email.invalid') },  // checks if it's a valid email format
                                    { max: 320, message: t('user-management.email.max.msg') },

                                ]}
                            >
                                <Input placeholder={t('user-management.email-add-placeholder')} disabled={isEdit} />
                            </Form.Item>

                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label={t('user-management.role')}
                                name="roleId"
                                rules={[
                                    { required: true, message: t('user-management.role.req.error') },  // required field

                                ]}>
                                <Select
                                    disabled={disableRole}
                                    showSearch={true}
                                    placeholder={t('user-management.select-role')}
                                    filterOption={(input, option) => {
                                        const children = option?.children as string | undefined;
                                        return children?.toLowerCase().includes(input.toLowerCase()) ?? false;
                                    }}
                                    allowClear
                                >
                                    {masterRoles.map((role) => (
                                        <Option key={role.id} value={role.id}>
                                            {role.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {!isSuperAdminRoleSelected &&
                            <Col span={12}>
                                <Form.Item
                                    label={t('user-management.ministry')}
                                    name="ministryId"
                                    rules={[
                                        { required: !isSuperAdminRoleSelected, message: t('user-management.ministry.mandatory') },
                                        {
                                            validator: async (_, selectedValues) => {
                                                // Ensure selectedValues is an array
                                                if (!Array.isArray(selectedValues)) {
                                                    return Promise.resolve(); // No validation needed if no value is selected
                                                }
                                                // Get selected parent ministries
                                                const selectedParents = selectedValues.filter((item: string) => {
                                                    const node = ministries.find((node) => node.id.toString() === item);
                                                    return node && node.parent_id === null;
                                                });
                                                // Check for multiple parent selections
                                                if (selectedParents.length > 1) {
                                                    return Promise.reject(
                                                        new Error(t('user-management.only-one-ministry'))
                                                    );
                                                }
                                                if (findTopLevelParents(ministries, selectedValues)?.length > 1) {
                                                    return Promise.reject(
                                                        new Error(t('user-management.only-one-parent'))
                                                    );
                                                }

                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <TreeSelect
                                        treeData={treeData}
                                        value={selectedMinistry}
                                        onChange={(newValues: string[] | null) => {
                                            if (!newValues || newValues.length === 0) {
                                                setSelectedMinistry(null);
                                            } else {
                                                setSelectedMinistry(newValues);
                                            }
                                        }}
                                        treeCheckable={true}
                                        showCheckedStrategy={SHOW_PARENT}
                                        placeholder={t('user-management.select-ministry')}
                                        allowClear
                                        showSearch
                                        filterTreeNode={(input, treeNode) => {
                                            const title = treeNode?.title;
                                            if (typeof title === 'string') {
                                                return title.toLowerCase().includes(input.toLowerCase());
                                            }
                                            return false;
                                        }}
                                        onSearch={(value) => {
                                            if (value) {
                                                setExpandedKeys(expandAllKeys(treeData)); // Expand all nodes during search
                                            } else {
                                                setExpandedKeys([]); // Collapse all nodes when search is cleared
                                            }
                                        }}
                                        onDropdownVisibleChange={(open) => {
                                            if (!open) {
                                                setExpandedKeys([]); // Collapse tree when dropdown is closed
                                            }
                                        }}
                                        treeExpandedKeys={expandedKeys}
                                        onTreeExpand={(keys) => setExpandedKeys(keys.map(String))} // Cast keys to strings
                                    />
                                </Form.Item>
                            </Col>}
                        <Divider style={{ margin: '15px 0 25px 0', borderBottom: '1px solid #CCC' }}></Divider>
                        <Col span={24} className='actionButHolder'>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}
                                    disabled={!isFormDirty}
                                >
                                    <PlusOutlined /> {isEdit ? t('btn.save') : t('user-management.add-user')}
                                </Button>
                                {isFormDirty ?
                                    <Popconfirm
                                        title={t('confirmation')}
                                        description={t('cancel.confirmation.msg')}
                                        okText={t('yes')}
                                        cancelText={t('no')}
                                        onConfirm={() => navigate('/setup/user-management')}>
                                        <Button type="primary" ><CloseOutlined /> {t('btn.cancel')}</Button>
                                    </Popconfirm>
                                    : <Button type="primary" onClick={() => navigate('/setup/user-management')}><CloseOutlined /> {t('btn.cancel')}</Button>}
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Row>
        </>
    );
};

export default AddEditUserComponent;